/**
 * @fileoverview File Manager component with directory tree and file operations
 * @module ctl-file-manager
 * @version 1.0.0
 *
 * @description
 * A client-side file manager component that renders a virtual file/folder tree.
 * Supports folder expansion, file selection, thumbnail previews, folder creation,
 * rename, delete, and move (via drag-and-drop). File system state is managed via
 * JavaScript property; actual persistence is the consumer's responsibility via events.
 *
 * @example
 * <ctl-file-manager id="fm"></ctl-file-manager>
 * <script type="module">
 *   const fm = document.querySelector('#fm');
 *   fm.tree = [
 *     { id: '1', name: 'Documents', type: 'folder', children: [
 *       { id: '2', name: 'Resume.pdf',  type: 'file', size: 12000, mime: 'application/pdf' },
 *       { id: '3', name: 'Notes.txt',   type: 'file', size:   340, mime: 'text/plain' },
 *     ]},
 *     { id: '4', name: 'Images', type: 'folder', children: [
 *       { id: '5', name: 'photo.jpg', type: 'file', size: 2000000, mime: 'image/jpeg',
 *         thumbnail: 'https://picsum.photos/seed/1/60/60' },
 *     ]},
 *   ];
 *   fm.addEventListener('ctl-fm-delete', e => console.log('delete', e.detail));
 *   fm.addEventListener('ctl-fm-rename', e => console.log('rename', e.detail));
 * </script>
 */

import { dispatchEvent } from '../../../utils/events.js';

const template = document.createElement('template');
template.innerHTML = `
<style>
  :host {
    display: flex;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
    height: 480px;
    min-height: 300px;
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    overflow: hidden;
    background: var(--ctl-color-surface);
    box-shadow: var(--ctl-shadow-card, 0 1px 4px rgba(0,0,0,.06));
  }

  /* ── Sidebar tree ─────────────────────────────────────────────────────── */
  .sidebar {
    width: 220px;
    flex-shrink: 0;
    border-right: 1px solid var(--ctl-color-border);
    overflow-y: auto;
    background: color-mix(in srgb, var(--ctl-color-surface-2, var(--ctl-color-surface)) 70%, var(--ctl-color-surface));
    display: flex;
    flex-direction: column;
  }

  .sidebar-header {
    padding: var(--ctl-space-2) var(--ctl-space-3);
    font-size: var(--ctl-font-size-xs);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--ctl-color-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .tree { flex: 1; padding: var(--ctl-space-1) 0; }

  .tree-item {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .tree-node {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-1);
    padding: 5px var(--ctl-space-2);
    padding-left: calc(var(--ctl-space-2) + var(--depth, 0) * 14px);
    cursor: pointer;
    border-radius: var(--ctl-radius-sm);
    font-size: var(--ctl-font-size-sm);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: none;
  }
  .tree-node:hover { background: color-mix(in srgb, var(--ctl-color-brand) 8%, transparent); }
  .tree-node[aria-selected="true"] {
    background: color-mix(in srgb, var(--ctl-color-brand) 14%, transparent);
    color: var(--ctl-color-brand);
    font-weight: var(--ctl-font-weight-medium, 500);
  }
  .tree-node:focus-visible { outline: none; box-shadow: var(--ctl-focus-ring); }

  .node-toggle {
    width: 14px;
    flex-shrink: 0;
    color: var(--ctl-color-muted);
    transition: transform var(--ctl-transition-fast);
  }
  .node-toggle[data-open="true"] { transform: rotate(90deg); }
  .node-toggle[data-leaf="true"] { opacity: 0; pointer-events: none; }

  .node-icon { flex-shrink: 0; color: var(--ctl-color-muted); }
  .node-icon.folder { color: var(--ctl-color-icon-folder); }
  .node-icon.image  { color: var(--ctl-color-icon-image); }

  .node-name { overflow: hidden; text-overflow: ellipsis; flex: 1; }

  /* ── Main content ─────────────────────────────────────────────────────── */
  .main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* toolbar */
  .toolbar {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
    padding: var(--ctl-space-2) var(--ctl-space-3);
    border-bottom: 1px solid var(--ctl-color-border);
    flex-wrap: wrap;
  }

  .breadcrumb {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
    overflow: hidden;
  }
  .crumb {
    all: unset;
    cursor: pointer;
    color: var(--ctl-color-brand);
    border-radius: var(--ctl-radius-sm);
  }
  .crumb:hover { text-decoration: underline; }
  .crumb:focus-visible { outline: 2px solid var(--ctl-color-brand); }
  .crumb-sep { color: var(--ctl-color-muted); }

  .tb-btn {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: var(--ctl-radius-md);
    font-size: var(--ctl-font-size-xs);
    font-weight: var(--ctl-font-weight-medium, 500);
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    white-space: nowrap;
    transition: background var(--ctl-transition-fast);
  }
  .tb-btn:hover { background: color-mix(in srgb, var(--ctl-color-border) 60%, var(--ctl-color-surface)); }
  .tb-btn:focus-visible { outline: none; box-shadow: var(--ctl-focus-ring); }
  .tb-btn.danger { color: var(--ctl-color-error, #dc2626); border-color: var(--ctl-color-error, #dc2626); }
  .tb-btn.danger:hover { background: color-mix(in srgb, var(--ctl-color-error, #dc2626) 8%, var(--ctl-color-surface)); }
  .tb-btn[disabled] { opacity: 0.4; pointer-events: none; }

  /* view toggle */
  .view-btn {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: var(--ctl-radius-sm);
    color: var(--ctl-color-muted);
    border: 1px solid transparent;
  }
  .view-btn:hover { background: color-mix(in srgb, var(--ctl-color-border) 60%, transparent); }
  .view-btn[aria-pressed="true"] {
    background: color-mix(in srgb, var(--ctl-color-brand) 10%, transparent);
    color: var(--ctl-color-brand);
    border-color: color-mix(in srgb, var(--ctl-color-brand) 30%, transparent);
  }
  .view-btn:focus-visible { outline: none; box-shadow: var(--ctl-focus-ring); }

  /* search */
  .search-input {
    all: unset;
    font-family: inherit;
    font-size: var(--ctl-font-size-sm);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    padding: 5px 10px;
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    width: 180px;
    box-sizing: border-box;
    transition: border-color var(--ctl-transition-fast), box-shadow var(--ctl-transition-fast);
  }
  .search-input::placeholder { color: var(--ctl-color-muted); }
  .search-input:focus { border-color: var(--ctl-color-brand); box-shadow: var(--ctl-focus-ring); outline: none; }

  /* file grid / list */
  .file-area {
    flex: 1;
    overflow-y: auto;
    padding: var(--ctl-space-3);
  }

  /* Grid view */
  .file-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: var(--ctl-space-3);
  }

  .file-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--ctl-space-1);
    padding: var(--ctl-space-2);
    border-radius: var(--ctl-radius-md);
    cursor: pointer;
    border: 2px solid transparent;
    text-align: center;
    transition: background var(--ctl-transition-fast), border-color var(--ctl-transition-fast);
    position: relative;
  }
  .file-tile:hover { background: color-mix(in srgb, var(--ctl-color-brand) 6%, transparent); }
  .file-tile[aria-selected="true"] {
    background: color-mix(in srgb, var(--ctl-color-brand) 12%, transparent);
    border-color: color-mix(in srgb, var(--ctl-color-brand) 40%, transparent);
  }
  .file-tile:focus-visible { outline: none; box-shadow: var(--ctl-focus-ring); }

  .tile-thumb {
    width: 60px;
    height: 60px;
    border-radius: var(--ctl-radius-sm);
    object-fit: cover;
    border: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface-2, #f9fafb);
  }

  .tile-icon {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--ctl-radius-sm);
    background: color-mix(in srgb, var(--ctl-color-border) 40%, transparent);
    font-size: var(--ctl-font-size-2xl-plus);
    color: var(--ctl-color-muted);
  }
  .tile-icon.folder { color: var(--ctl-color-icon-folder); background: color-mix(in srgb, var(--ctl-color-icon-folder) 10%, transparent); }

  .tile-name {
    font-size: var(--ctl-font-size-xs);
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    word-break: break-all;
  }

  /* List view */
  .file-list { display: flex; flex-direction: column; gap: 2px; }

  .file-row {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
    padding: 7px var(--ctl-space-2);
    border-radius: var(--ctl-radius-sm);
    cursor: pointer;
    border: 1px solid transparent;
    transition: background var(--ctl-transition-fast);
  }
  .file-row:hover { background: color-mix(in srgb, var(--ctl-color-brand) 6%, transparent); }
  .file-row[aria-selected="true"] {
    background: color-mix(in srgb, var(--ctl-color-brand) 10%, transparent);
    border-color: color-mix(in srgb, var(--ctl-color-brand) 25%, transparent);
  }
  .file-row:focus-visible { outline: none; box-shadow: var(--ctl-focus-ring); }

  .row-icon {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--ctl-radius-sm);
    background: color-mix(in srgb, var(--ctl-color-border) 40%, transparent);
    font-size: var(--ctl-font-size-body);
    flex-shrink: 0;
    color: var(--ctl-color-muted);
  }
  .row-icon.folder { color: var(--ctl-color-icon-folder); background: color-mix(in srgb, var(--ctl-color-icon-folder) 10%, transparent); }

  .row-thumb {
    width: 28px;
    height: 28px;
    border-radius: var(--ctl-radius-sm);
    object-fit: cover;
    border: 1px solid var(--ctl-color-border);
    flex-shrink: 0;
  }

  .row-name { flex: 1; font-size: var(--ctl-font-size-sm); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .row-meta { font-size: var(--ctl-font-size-xs); color: var(--ctl-color-muted); white-space: nowrap; }

  /* context menu */
  .ctx-menu {
    display: none;
    position: fixed;
    z-index: var(--ctl-z-dropdown, 1000);
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    box-shadow: var(--ctl-shadow-md);
    min-width: 160px;
    padding: var(--ctl-space-1) 0;
  }
  .ctx-menu[data-open="true"] { display: block; }

  .ctx-item {
    all: unset;
    display: flex;
    align-items: center;
    gap: var(--ctl-space-2);
    width: 100%;
    padding: 8px 14px;
    font-size: var(--ctl-font-size-sm);
    cursor: pointer;
    box-sizing: border-box;
    color: var(--ctl-color-text);
    border-radius: 0;
  }
  .ctx-item:hover { background: color-mix(in srgb, var(--ctl-color-brand) 8%, transparent); }
  .ctx-item:focus-visible { outline: none; box-shadow: inset 0 0 0 2px var(--ctl-color-brand); }
  .ctx-item.danger { color: var(--ctl-color-error, #dc2626); }
  .ctx-item.danger:hover { background: color-mix(in srgb, var(--ctl-color-error, #dc2626) 8%, transparent); }

  /* inline rename */
  .rename-input {
    all: unset;
    font-family: inherit;
    font-size: var(--ctl-font-size-xs);
    border: 1px solid var(--ctl-color-brand);
    border-radius: var(--ctl-radius-sm);
    padding: 2px 6px;
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    width: 90px;
    box-sizing: border-box;
  }

  /* empty state */
  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: var(--ctl-color-muted);
    gap: var(--ctl-space-2);
    font-size: var(--ctl-font-size-sm);
  }

  /* status bar */
  .status-bar {
    padding: var(--ctl-space-1) var(--ctl-space-3);
    border-top: 1px solid var(--ctl-color-border);
    font-size: var(--ctl-font-size-xs);
    color: var(--ctl-color-muted);
    background: color-mix(in srgb, var(--ctl-color-surface-2, var(--ctl-color-surface)) 70%, var(--ctl-color-surface));
  }

  .sr-only {
    position: absolute;
    width: 1px; height: 1px;
    padding: 0; margin: -1px;
    overflow: hidden; clip: rect(0,0,0,0);
    white-space: nowrap; border: 0;
  }
</style>

<!-- SIDEBAR -->
<div class="sidebar" part="sidebar" role="navigation" aria-label="Folder tree">
  <div class="sidebar-header">
    <span>Files</span>
    <button class="tb-btn new-folder-btn" type="button" style="border:none; background:none; padding:2px 4px;" title="New folder" aria-label="New folder">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M1 3a1 1 0 011-1h4l2 2h6a1 1 0 011 1v8a1 1 0 01-1 1H2a1 1 0 01-1-1V3z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
        <path d="M8 7v4M6 9h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
    </button>
  </div>
  <ul class="tree" role="tree" aria-label="Folder tree"></ul>
</div>

<!-- MAIN -->
<div class="main" part="main">
  <div class="toolbar" part="toolbar">
    <nav class="breadcrumb" aria-label="Current path"></nav>

    <input class="search-input" type="search" placeholder="Search files…" aria-label="Search files">

    <button class="tb-btn" type="button" data-action="new-folder" aria-label="New folder">
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M1 3a1 1 0 011-1h4l2 2h6a1 1 0 011 1v8a1 1 0 01-1 1H2a1 1 0 01-1-1V3z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
        <path d="M8 7v4M6 9h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      New Folder
    </button>

    <button class="tb-btn danger" type="button" data-action="delete" disabled aria-label="Delete selected">
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M2 4h12M5 4V2h6v2M6 7v5M10 7v5M3 4l1 10h8l1-10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      Delete
    </button>

    <div style="display:flex; gap:4px; margin-left:auto;">
      <button class="view-btn" data-view="grid" aria-pressed="true" aria-label="Grid view" title="Grid view">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.5"/>
          <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.5"/>
          <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.5"/>
          <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" stroke-width="1.5"/>
        </svg>
      </button>
      <button class="view-btn" data-view="list" aria-pressed="false" aria-label="List view" title="List view">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M1 4h14M1 8h14M1 12h14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    </div>
  </div>

  <div class="file-area" part="file-area" role="region" aria-label="File contents"></div>

  <div class="status-bar" part="status-bar" aria-live="polite"></div>
</div>

<!-- Context menu -->
<div class="ctx-menu" role="menu" aria-label="File actions">
  <button class="ctx-item" role="menuitem" data-ctx="open">
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 4h12v10H2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M2 4l6-2 6 2" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
    Open
  </button>
  <button class="ctx-item" role="menuitem" data-ctx="rename">
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M11 2l3 3-9 9H2v-3L11 2z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
    Rename
  </button>
  <button class="ctx-item danger" role="menuitem" data-ctx="delete">
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 4h12M5 4V2h6v2M3 4l1 10h8l1-10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
    Delete
  </button>
</div>

<div class="sr-only" aria-live="polite" aria-atomic="true"></div>
`;

export class CtlFileManager extends HTMLElement {
  static get observedAttributes() {
    return ['view'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' }).appendChild(template.content.cloneNode(true));

    this._tree = [];           // full tree data
    this._flatMap = new Map(); // id → node
    this._openFolders = new Set();
    this._currentFolderId = null; // null = root
    this._selectedId = null;
    this._searchQuery = '';
    this._view = 'grid';       // 'grid' | 'list'
    this._renamingId = null;
    this._ctxTarget = null;

    this._sidebarTree = this.shadowRoot.querySelector('.tree');
    this._fileArea = this.shadowRoot.querySelector('.file-area');
    this._breadcrumb = this.shadowRoot.querySelector('.breadcrumb');
    this._statusBar = this.shadowRoot.querySelector('.status-bar');
    this._ctxMenu = this.shadowRoot.querySelector('.ctx-menu');
    this._searchInput = this.shadowRoot.querySelector('.search-input');
    this._deleteBtn = this.shadowRoot.querySelector('[data-action="delete"]');
    this._live = this.shadowRoot.querySelector('[aria-live]');

    // Bound reference for proper add/remove listener pairing
    this._boundDocClick = this._onDocClick.bind(this);
  }

  connectedCallback() {
    this.shadowRoot.querySelector('[data-action="new-folder"]').addEventListener('click', () => this._createFolder());
    this.shadowRoot.querySelector('.new-folder-btn').addEventListener('click', () => this._createFolder());
    this._deleteBtn.addEventListener('click', () => this._deleteSelected());

    this.shadowRoot.querySelectorAll('.view-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this._view = btn.dataset.view;
        this.shadowRoot.querySelectorAll('.view-btn').forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
        this._renderFileArea();
      });
    });

    this._searchInput.addEventListener('input', e => {
      this._searchQuery = e.target.value.trim().toLowerCase();
      this._renderFileArea();
    });

    this._ctxMenu.addEventListener('click', e => {
      const item = e.target.closest('[data-ctx]');
      if (!item || !this._ctxTarget) return;
      this._hideCtx();
      const action = item.dataset.ctx;
      if (action === 'open') this._openNode(this._ctxTarget);
      if (action === 'rename') this._startRename(this._ctxTarget);
      if (action === 'delete') this._deleteNode(this._ctxTarget);
    });

    document.addEventListener('click', this._boundDocClick);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') this._hideCtx(); });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._boundDocClick);
  }

  attributeChangedCallback(name, _old, val) {
    if (name === 'view') {
      this._view = val === 'list' ? 'list' : 'grid';
      this._renderFileArea();
    }
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  get tree() { return this._tree; }
  set tree(val) {
    this._tree = Array.isArray(val) ? val : [];
    this._buildFlatMap(this._tree, null);
    this._currentFolderId = null;
    this._selectedId = null;
    this._render();
  }

  // ─── Internal ─────────────────────────────────────────────────────────────

  _buildFlatMap(nodes, parentId) {
    nodes.forEach(node => {
      this._flatMap.set(node.id, { ...node, parentId });
      if (node.children) this._buildFlatMap(node.children, node.id);
    });
  }

  _render() {
    this._renderSidebar();
    this._renderFileArea();
    this._renderBreadcrumb();
  }

  _renderSidebar() {
    this._sidebarTree.innerHTML = '';
    this._renderSidebarLevel(this._tree, this._sidebarTree, 0);
  }

  _renderSidebarLevel(nodes, container, depth) {
    nodes.filter(n => n.type === 'folder').forEach(node => {
      const li = document.createElement('li');
      li.className = 'tree-item';
      li.setAttribute('role', 'treeitem');

      const div = document.createElement('div');
      div.className = 'tree-node';
      div.setAttribute('tabindex', '0');
      div.setAttribute('role', 'button');
      div.setAttribute('aria-selected', String(this._currentFolderId === node.id));
      div.style.setProperty('--depth', String(depth));

      const isOpen = this._openFolders.has(node.id);
      const hasChildren = node.children?.some(c => c.type === 'folder');

      div.innerHTML = `
        <svg class="node-toggle" data-open="${isOpen}" data-leaf="${!hasChildren}" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M3 2l5 4-5 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg class="node-icon folder" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M1 3a1 1 0 011-1h4l2 2h6a1 1 0 011 1v8a1 1 0 01-1 1H2a1 1 0 01-1-1V3z" fill="currentColor" opacity=".2"/>
          <path d="M1 3a1 1 0 011-1h4l2 2h6a1 1 0 011 1v8a1 1 0 01-1 1H2a1 1 0 01-1-1V3z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
        </svg>
        <span class="node-name">${_escapeHtml(node.name)}</span>
      `;

      div.addEventListener('click', () => this._navigateTo(node.id));
      div.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._navigateTo(node.id); }
      });
      div.addEventListener('contextmenu', e => { e.preventDefault(); this._showCtx(e, node); });

      li.appendChild(div);

      if (hasChildren && isOpen) {
        const sub = document.createElement('ul');
        sub.setAttribute('role', 'group');
        this._renderSidebarLevel(node.children.filter(c => c.type === 'folder'), sub, depth + 1);
        li.appendChild(sub);
      }

      container.appendChild(li);
    });
  }

  _renderBreadcrumb() {
    const path = this._getPath(this._currentFolderId);
    this._breadcrumb.innerHTML = '';

    const rootBtn = document.createElement('button');
    rootBtn.className = 'crumb';
    rootBtn.textContent = 'Home';
    rootBtn.addEventListener('click', () => this._navigateTo(null));
    this._breadcrumb.appendChild(rootBtn);

    path.forEach((node, i) => {
      const sep = document.createElement('span');
      sep.className = 'crumb-sep';
      sep.textContent = '/';
      sep.setAttribute('aria-hidden', 'true');
      this._breadcrumb.appendChild(sep);

      const btn = document.createElement('button');
      btn.className = 'crumb';
      btn.textContent = node.name;
      if (i < path.length - 1) {
        btn.addEventListener('click', () => this._navigateTo(node.id));
      } else {
        btn.setAttribute('aria-current', 'location');
        btn.style.color = 'var(--ctl-color-text)';
        btn.style.cursor = 'default';
      }
      this._breadcrumb.appendChild(btn);
    });
  }

  _renderFileArea() {
    this._fileArea.innerHTML = '';

    const currentFolder = this._currentFolderId
      ? this._flatMap.get(this._currentFolderId)
      : null;

    let children = currentFolder
      ? (currentFolder.children || [])
      : this._tree;

    if (this._searchQuery) {
      children = this._flatSearch(this._tree, this._searchQuery);
    }

    if (!children.length) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.innerHTML = `
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
          <rect x="5" y="8" width="30" height="26" rx="3" stroke="currentColor" stroke-width="1.5" stroke-dasharray="4 2"/>
          <path d="M5 8l7-5h16l7 5" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
        </svg>
        <span>This folder is empty</span>
      `;
      this._fileArea.appendChild(empty);
      this._updateStatus(0);
      return;
    }

    const container = document.createElement('div');
    container.className = this._view === 'grid' ? 'file-grid' : 'file-list';
    container.setAttribute('role', 'listbox');
    container.setAttribute('aria-label', 'Files');
    container.setAttribute('aria-multiselectable', 'false');

    children.forEach(node => {
      const el = this._view === 'grid' ? this._makeTile(node) : this._makeRow(node);
      container.appendChild(el);
    });

    this._fileArea.appendChild(container);
    this._updateStatus(children.length);
  }

  _makeTile(node) {
    const div = document.createElement('div');
    div.className = 'file-tile';
    div.setAttribute('tabindex', '0');
    div.setAttribute('role', 'option');
    div.setAttribute('aria-selected', String(this._selectedId === node.id));
    div.setAttribute('aria-label', node.name);
    div.dataset.nodeId = node.id;

    if (node.thumbnail && _isImage(node.mime)) {
      div.innerHTML = `<img class="tile-thumb" src="${_escapeHtml(node.thumbnail)}" alt="" loading="lazy"><span class="tile-name">${_escapeHtml(node.name)}</span>`;
    } else {
      const iconHtml = `<div class="tile-icon ${node.type === 'folder' ? 'folder' : ''}">${_fileIcon(node)}</div>`;
      div.innerHTML = `${iconHtml}<span class="tile-name">${_escapeHtml(node.name)}</span>`;
    }

    div.addEventListener('click', e => { e.stopPropagation(); this._selectNode(node.id); });
    div.addEventListener('dblclick', () => this._openNode(node));
    div.addEventListener('keydown', e => {
      if (e.key === 'Enter') this._openNode(node);
      if (e.key === 'Delete' || e.key === 'Backspace') this._deleteNode(node);
      if (e.key === 'F2') this._startRename(node);
    });
    div.addEventListener('contextmenu', e => { e.preventDefault(); this._showCtx(e, node); });

    // drag-and-drop for move
    div.setAttribute('draggable', 'true');
    div.addEventListener('dragstart', e => { e.dataTransfer.setData('text/plain', node.id); e.dataTransfer.effectAllowed = 'move'; });
    if (node.type === 'folder') {
      div.addEventListener('dragover', e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; div.style.outline = '2px solid var(--ctl-color-brand)'; });
      div.addEventListener('dragleave', () => { div.style.outline = ''; });
      div.addEventListener('drop', e => {
        e.preventDefault(); div.style.outline = '';
        const srcId = e.dataTransfer.getData('text/plain');
        if (srcId && srcId !== node.id) this._moveNode(srcId, node.id);
      });
    }

    return div;
  }

  _makeRow(node) {
    const div = document.createElement('div');
    div.className = 'file-row';
    div.setAttribute('tabindex', '0');
    div.setAttribute('role', 'option');
    div.setAttribute('aria-selected', String(this._selectedId === node.id));
    div.setAttribute('aria-label', node.name);
    div.dataset.nodeId = node.id;

    let iconHtml;
    if (node.thumbnail && _isImage(node.mime)) {
      iconHtml = `<img class="row-thumb" src="${_escapeHtml(node.thumbnail)}" alt="" loading="lazy">`;
    } else {
      iconHtml = `<div class="row-icon ${node.type === 'folder' ? 'folder' : ''}">${_fileIcon(node)}</div>`;
    }

    const size = node.type === 'file' ? _formatSize(node.size) : '';
    div.innerHTML = `
      ${iconHtml}
      <span class="row-name">${_escapeHtml(node.name)}</span>
      <span class="row-meta">${size}</span>
    `;

    div.addEventListener('click', e => { e.stopPropagation(); this._selectNode(node.id); });
    div.addEventListener('dblclick', () => this._openNode(node));
    div.addEventListener('keydown', e => {
      if (e.key === 'Enter') this._openNode(node);
      if (e.key === 'Delete' || e.key === 'Backspace') this._deleteNode(node);
      if (e.key === 'F2') this._startRename(node);
    });
    div.addEventListener('contextmenu', e => { e.preventDefault(); this._showCtx(e, node); });

    return div;
  }

  // ─── File ops ─────────────────────────────────────────────────────────────

  _navigateTo(folderId) {
    if (folderId && this._flatMap.get(folderId)?.type !== 'folder') return;
    if (folderId) this._openFolders.add(folderId);
    this._currentFolderId = folderId;
    this._selectedId = null;
    this._deleteBtn.disabled = true;
    this._searchInput.value = '';
    this._searchQuery = '';
    this._render();
    dispatchEvent(this, 'ctl-fm-navigate', { folderId });
  }

  _selectNode(nodeId) {
    this._selectedId = nodeId;
    this._deleteBtn.disabled = false;
    // Update aria-selected
    this._fileArea.querySelectorAll('[role="option"]').forEach(el => {
      el.setAttribute('aria-selected', String(el.dataset.nodeId === nodeId));
    });
    dispatchEvent(this, 'ctl-fm-select', { nodeId, node: this._flatMap.get(nodeId) });
  }

  _openNode(node) {
    if (node.type === 'folder') {
      this._navigateTo(node.id);
    } else {
      dispatchEvent(this, 'ctl-fm-open', { nodeId: node.id, node });
    }
  }

  _deleteSelected() {
    if (!this._selectedId) return;
    const node = this._flatMap.get(this._selectedId);
    if (node) this._deleteNode(node);
  }

  _deleteNode(node) {
    this._removeFromTree(this._tree, node.id);
    this._flatMap.delete(node.id);
    if (this._selectedId === node.id) {
      this._selectedId = null;
      this._deleteBtn.disabled = true;
    }
    this._render();
    this._live.textContent = `${node.name} deleted.`;
    dispatchEvent(this, 'ctl-fm-delete', { nodeId: node.id, node });
  }

  _startRename(node) {
    this._renamingId = node.id;
    const nameEl = this._fileArea.querySelector(`[data-node-id="${node.id}"] .tile-name, [data-node-id="${node.id}"] .row-name`);
    if (!nameEl) return;

    const input = document.createElement('input');
    input.className = 'rename-input';
    input.value = node.name;
    input.setAttribute('aria-label', 'Rename');
    nameEl.replaceWith(input);
    input.select();

    const commit = () => {
      const oldName = node.name;
      const newName = input.value.trim();
      if (newName && newName !== oldName) {
        const target = this._flatMap.get(node.id);
        if (target) target.name = newName;
        this._updateNameInTree(this._tree, node.id, newName);
        dispatchEvent(this, 'ctl-fm-rename', { nodeId: node.id, oldName, newName });
        this._live.textContent = `Renamed to ${newName}.`;
      }
      this._renamingId = null;
      this._render();
    };

    input.addEventListener('blur', commit);
    input.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      if (e.key === 'Escape') { this._renamingId = null; this._render(); }
    });
  }

  _createFolder() {
    const newId = `folder-${Date.now()}`;
    const newFolder = { id: newId, name: 'New Folder', type: 'folder', children: [] };

    if (this._currentFolderId) {
      const parent = this._flatMap.get(this._currentFolderId);
      if (parent) {
        if (!parent.children) parent.children = [];
        parent.children.unshift(newFolder);
      }
    } else {
      this._tree.unshift(newFolder);
    }
    this._flatMap.set(newId, { ...newFolder, parentId: this._currentFolderId });

    this._render();
    dispatchEvent(this, 'ctl-fm-create-folder', { nodeId: newId, parentId: this._currentFolderId });

    // Auto-start rename
    requestAnimationFrame(() => {
      const el = this._fileArea.querySelector(`[data-node-id="${newId}"]`);
      if (el) el.dispatchEvent(new KeyboardEvent('keydown', { key: 'F2', bubbles: true }));
    });
  }

  _moveNode(srcId, targetFolderId) {
    const node = this._flatMap.get(srcId);
    if (!node) return;
    if (srcId === targetFolderId) return;

    // Remove from current parent
    this._removeFromTree(this._tree, srcId);

    // Add to target
    const target = this._flatMap.get(targetFolderId);
    if (target) {
      if (!target.children) target.children = [];
      const { parentId: _p, ...nodeData } = node;
      target.children.push(nodeData);
      this._flatMap.set(srcId, { ...nodeData, parentId: targetFolderId });
    }

    this._render();
    dispatchEvent(this, 'ctl-fm-move', { nodeId: srcId, targetFolderId });
  }

  // ─── Context menu ─────────────────────────────────────────────────────────

  _showCtx(e, node) {
    this._ctxTarget = node;
    this._selectNode(node.id);
    const menu = this._ctxMenu;
    menu.dataset.open = 'true';
    const vw = window.innerWidth, vh = window.innerHeight;
    const mw = 180, mh = 130;
    const left = Math.min(e.clientX, vw - mw - 8);
    const top = Math.min(e.clientY, vh - mh - 8);
    menu.style.left = `${left}px`;
    menu.style.top = `${top}px`;

    const firstItem = menu.querySelector('.ctx-item');
    if (firstItem) requestAnimationFrame(() => firstItem.focus());
  }

  _hideCtx() {
    this._ctxMenu.dataset.open = 'false';
    this._ctxTarget = null;
  }

  _onDocClick(e) {
    if (this._ctxMenu.dataset.open !== 'true') return;
    if (!this.shadowRoot.contains(e.target) && !this.contains(e.target)) this._hideCtx();
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  _getPath(folderId) {
    const path = [];
    let id = folderId;
    while (id) {
      const node = this._flatMap.get(id);
      if (!node) break;
      path.unshift(node);
      id = node.parentId;
    }
    return path;
  }

  _removeFromTree(nodes, id) {
    const idx = nodes.findIndex(n => n.id === id);
    if (idx !== -1) { nodes.splice(idx, 1); return true; }
    return nodes.some(n => n.children && this._removeFromTree(n.children, id));
  }

  _updateNameInTree(nodes, id, name) {
    const node = nodes.find(n => n.id === id);
    if (node) { node.name = name; return true; }
    return nodes.some(n => n.children && this._updateNameInTree(n.children, id, name));
  }

  _flatSearch(nodes, query) {
    const results = [];
    const walk = list => {
      list.forEach(node => {
        if (node.name.toLowerCase().includes(query)) results.push(node);
        if (node.children) walk(node.children);
      });
    };
    walk(nodes);
    return results;
  }

  _updateStatus(count) {
    this._statusBar.textContent = count === 0
      ? 'Empty folder'
      : `${count} item${count !== 1 ? 's' : ''}${this._selectedId ? ' · 1 selected' : ''}`;
  }
}

// ─── Icon helpers ─────────────────────────────────────────────────────────────

function _isImage(mime) {
  return typeof mime === 'string' && mime.startsWith('image/');
}

function _fileIcon(node) {
  if (node.type === 'folder') {
    return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M2 4a1 1 0 011-1h5l2 2h7a1 1 0 011 1v9a1 1 0 01-1 1H3a1 1 0 01-1-1V4z" fill="currentColor" opacity=".3"/><path d="M2 4a1 1 0 011-1h5l2 2h7a1 1 0 011 1v9a1 1 0 01-1 1H3a1 1 0 01-1-1V4z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>`;
  }
  const mime = node.mime || '';
  if (_isImage(mime)) return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><rect x="2" y="3" width="16" height="14" rx="2" stroke="currentColor" stroke-width="1.2"/><circle cx="7" cy="8" r="1.5" fill="currentColor"/><path d="M2 14l5-5 4 4 2-2 5 3" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>`;
  if (mime === 'application/pdf') return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 2h9l5 5v11a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M13 2v5h5" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M7 13h6M7 10h6" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>`;
  if (mime.startsWith('text/')) return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 2h9l5 5v11a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M13 2v5h5" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M7 10h6M7 13h4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>`;
  // Generic file
  return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 2h9l5 5v11a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M13 2v5h5" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>`;
}

function _formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function _escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

if (!customElements.get('ctl-file-manager')) {
  customElements.define('ctl-file-manager', CtlFileManager);
}
